import React, { useState } from 'react';
import { Sparkles, User, BookOpen, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { callClaude } from './anthropic';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';

function ReaderInsightCard({ reader, reviews, books }) {
  const [insight, setInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);

  const generateInsight = async () => {
    setIsLoading(true);
    setError(null);

    // Build reader profile for Claude
    const readerReviews = reviews.filter(
      (rev) => rev.readerEmail === reader.emailCorrespondence
    );

    const profile = {
      name: reader.name || 'Unknown',
      favoriteBooks: reader.favoriteBooks || '',
      genres: reader.preferences?.favoriteGenres || [],
      tropes: reader.preferences?.favoriteTropes || '',
      platforms: reader.reviewPlatforms || [],
      location: reader.location || '',
      status: reader.status || '',
      rating: reader.rating || '',
      booksReceived: reader.booksReceived || [],
      reviewCount: readerReviews.length,
      reviews: readerReviews.slice(0, 3).map((r) => ({
        bookTitle: r.bookTitle || '',
        rating: r.rating || '',
        summary: r.quickSummary || '',
        lovedMost: r.lovedMost || '',
        favoriteQuote: r.favoriteQuote || '',
        readerPath: r.readerPath || '',
      })),
    };

    const bookList = books.map((b) => ({
      title: b.title,
      series: b.series || '',
      genres: b.genres || '',
      blurb: b.blurb ? b.blurb.slice(0, 200) : '',
    }));

    const systemPrompt = `You are a warm, insightful literary matchmaker for an indie author's ARC (Advance Review Copy) program called Narratoria. You analyse reader profiles and their review history to create personalised reading insights.

Your tone is enthusiastic but genuine — like a knowledgeable bookshop owner who remembers every customer. Use the reader's first name. Be specific about WHY you're making each suggestion based on their actual data.

Return a JSON object with these fields:
- readerPersona: string (a fun 2-3 word label like "Romantasy Devotee" or "Dark Fantasy Connoisseur" or "Morally Grey Enthusiast")
- personalityInsight: string (2-3 sentences about what kind of reader they are based on their reviews, ratings, and preferences — be specific, reference their actual data)
- bookMatch: string (which of the author's books would suit them best and WHY — reference specific tropes/genres they love)
- engagementTip: string (1-2 sentences — a specific suggestion for how the author could deepen this reader's engagement based on their platform presence, review style, and reader path preference)
- nextBookPrediction: string (1-2 sentences — what kind of book they'd love next based on their taste profile, be creative and specific about tropes/themes)
- strengthBadges: array of strings (2-4 short badges like "Detailed Reviewer", "Street Team Material", "Cross-Platform Poster", "Early Adopter", "Quote Finder", "High Engagement" — only include ones genuinely supported by the data)

Return ONLY the JSON object, no markdown fences.`;

    const prompt = `Reader profile:
${JSON.stringify(profile, null, 2)}

Author's books:
${JSON.stringify(bookList, null, 2)}

Generate personalised reading insights for this reader.`;

    try {
      let text = await callClaude({ systemPrompt, prompt, maxTokens: 800 });
      // Strip markdown fences if Claude wraps the JSON
      text = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
      const parsed = JSON.parse(text);
      setInsight(parsed);
      setIsExpanded(true);
    } catch (err) {
      setError('Could not generate insights. ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const hasData =
    reader.favoriteBooks ||
    reader.preferences?.favoriteGenres?.length > 0 ||
    reader.preferences?.favoriteTropes ||
    reviews.some((r) => r.readerEmail === reader.emailCorrespondence);

  return (
    <Card className="overflow-hidden rounded-2xl border border-slate-100 transition-all hover:shadow-md">
      {/* Reader Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-white p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white shadow-sm">
            <User className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{reader.name || reader.emailCorrespondence}</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {reader.location && <span>{reader.location}</span>}
              {reader.status && (
                <Badge className="bg-slate-100 text-teal-700 text-[10px]">{reader.status}</Badge>
              )}
              {reader.rating && <span>{'★'.repeat(Math.round(Number(reader.rating)))} {reader.rating}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {insight && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8 p-0 text-slate-400"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
          <Button
            onClick={generateInsight}
            disabled={isLoading}
            size="sm"
            className={`gap-1.5 text-xs ${
              insight
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700'
            }`}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Analysing...
              </>
            ) : insight ? (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                Generate Insight
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>
      )}

      {/* No Data Hint */}
      {!hasData && !insight && !isLoading && (
        <div className="border-t border-slate-50 bg-slate-50/50 px-4 py-2 text-xs text-teal-600">
          Limited data — insights improve with reviews, genres, and favourite books
        </div>
      )}

      {/* Insight Results */}
      {insight && isExpanded && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {/* Persona + Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-gradient-to-r from-violet-100 to-purple-100 text-purple-800 text-sm font-bold px-3 py-1">
              {insight.readerPersona}
            </Badge>
            {insight.strengthBadges?.map((badge) => (
              <Badge key={badge} className="bg-slate-100 text-slate-600 text-[10px]">
                {badge}
              </Badge>
            ))}
          </div>

          {/* Personality */}
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-purple-600">Reader Profile</p>
            <p className="text-sm text-slate-700 leading-relaxed">{insight.personalityInsight}</p>
          </div>

          {/* Book Match */}
          <div className="rounded-xl bg-gradient-to-br from-slate-50 to-orange-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-teal-600">
              <BookOpen className="mr-1 inline h-3.5 w-3.5" />
              Best Book Match
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{insight.bookMatch}</p>
          </div>

          {/* Next Book Prediction */}
          <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-600">
              <Sparkles className="mr-1 inline h-3.5 w-3.5" />
              What They'd Love Next
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{insight.nextBookPrediction}</p>
          </div>

          {/* Engagement Tip */}
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-600">Engagement Tip</p>
            <p className="text-sm text-slate-700 leading-relaxed">{insight.engagementTip}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ReaderInsights({ readers, reviews, books }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [generatingAll, setGeneratingAll] = useState(false);

  const filtered = readers.filter((r) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      r.name?.toLowerCase().includes(term) ||
      r.emailCorrespondence?.toLowerCase().includes(term) ||
      r.location?.toLowerCase().includes(term);
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-200">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Reader Insights</h2>
            <p className="text-sm text-slate-500">
              AI-powered reading profiles and personalised recommendations for your ARC readers
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-slate-100 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search readers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-slate-200 py-2 pl-3 pr-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-slate-100"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-400"
          >
            <option value="all">All statuses</option>
            <option value="Noted ARC interest">Noted ARC interest</option>
            <option value="Engaged">Engaged</option>
            <option value="ARC sent">ARC sent</option>
            <option value="Reviewed">Reviewed</option>
            <option value="Street Team">Street Team</option>
            <option value="Book 2 ARC interest">Book 2 ARC interest</option>
          </select>
          <Badge className="bg-slate-100 text-slate-600">
            {filtered.length} reader{filtered.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </Card>

      {/* Reader Cards */}
      {filtered.length === 0 ? (
        <Card className="rounded-2xl p-12 text-center">
          <Sparkles className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="text-slate-500">No readers match your search</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((reader) => (
            <ReaderInsightCard
              key={reader.id || reader.emailCorrespondence}
              reader={reader}
              reviews={reviews}
              books={books}
            />
          ))}
        </div>
      )}
    </div>
  );
}
